/**
 * Dictionary Service
 * API calls for dictionary word lookup
 */

import { baseApiClient } from './apiClient';
import { PhoneticSystem } from '../types/auth';

const LOCALE = process.env.REACT_APP_LOCALE || 'zh-TW';

/**
 * Word form information from exchange field
 */
export interface WordForms {
  lemma?: string;           // Base word (for inflected forms)
  formType?: string;        // Form type of this word (p = past, d = past participle, etc.)
  past?: string;            // Past tense
  pastParticiple?: string;  // Past participle
  progressive?: string;     // Progressive/gerund (-ing)
  thirdPerson?: string;     // Third person singular
  plural?: string;          // Plural form
  comparative?: string;     // Comparative
  superlative?: string;     // Superlative
}

/**
 * Heteronym variant - different pronunciations for the same spelling
 */
export interface HeteronymVariant {
  phonetic: string;
  partOfSpeech: string;
  meaning_native: string;
  meaning_en: string;
  context?: string;
  tense?: string;
}

/**
 * Heteronym data - words with multiple pronunciations (同形異音詞)
 */
export interface HeteronymData {
  word: string;
  variants: HeteronymVariant[];
  note_native?: string;
}

/**
 * AI-generated example sentence for a specific word meaning
 */
export interface DefinitionExample {
  pos: string;           // Part of speech (n., v., adj., etc.)
  meaning_native: string;  // Native language meaning
  example_en: string;      // English example sentence
  example_native: string;  // Native language translation of example
}

/**
 * Compound-word result: when "gene-edited" isn't in the dictionary, the backend
 * splits on "-" and returns each part's lookup under `compound.parts`, which the
 * popover renders as a stacked layout.
 */
export interface CompoundLookup {
  parts: DictionaryWord[];
}

export interface DictionaryWord {
  word: string;
  phonetic?: string;
  pos?: string;          // Part of speech
  definition?: string;
  translation?: string;
  collins?: number;
  oxford?: number;
  tag?: string;
  bnc?: number;
  frq?: number;
  exchange?: string;
  found?: boolean;
  cefrLevel?: string | null;
  audio?: Array<{
    phonetic: string;
    url: string;
  }>;
  lemmaTranslation?: string; // Translation of base word for inflected forms (e.g., "keep" for "kept")
  wordForms?: WordForms;     // Word forms from exchange field
  heteronym?: HeteronymData; // Heteronym variants if applicable (同形異音詞)
  examples?: DefinitionExample[]; // AI-generated example sentences
  compound?: CompoundLookup | null; // Sub-word results for hyphenated compounds like "gene-edited"
}

export interface DictionarySearchResult {
  word: string;
  phonetic?: string;
}

export interface LookupOptions {
  generateAudio?: boolean;
  phoneticSystem?: PhoneticSystem;
  locale?: string;
  includeExamples?: boolean;
}

/**
 * Get user's phonetic system preference from localStorage
 */
function getUserPhoneticSystem(): PhoneticSystem | undefined {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.phoneticSystem;
    }
  } catch {
    // Ignore parsing errors
  }
  return undefined;
}

class DictionaryService {
  /**
   * Lookup a single word
   * @param word - Word to look up
   * @param options - Lookup options (generateAudio, phoneticSystem, locale, includeExamples)
   */
  async lookup(word: string, options: LookupOptions = {}): Promise<DictionaryWord> {
    const {
      generateAudio = true,
      phoneticSystem = getUserPhoneticSystem(),
      locale = LOCALE,
      includeExamples = false
    } = options;

    try {
      const response = await baseApiClient.get(`/dictionary/lookup/${encodeURIComponent(word)}`, {
        params: {
          audio: generateAudio,
          locale,
          ...(phoneticSystem && { phonetic: phoneticSystem }),
          ...(includeExamples && { examples: 'true' })
        }
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Word "${word}" not found in dictionary`);
      }
      throw error;
    }
  }

  /**
   * Batch lookup multiple words
   */
  async batchLookup(words: string[], options: LookupOptions = {}): Promise<DictionaryWord[]> {
    const {
      generateAudio = false,
      phoneticSystem = getUserPhoneticSystem(),
      locale = LOCALE
    } = options;

    try {
      const params = new URLSearchParams();
      params.set('locale', locale);
      if (generateAudio) params.set('audio', 'true');
      if (phoneticSystem) params.set('phonetic', phoneticSystem);

      const response = await baseApiClient.post(`/dictionary/batch?${params.toString()}`, { words });
      return response.data;
    } catch (error) {
      console.error('Batch lookup error:', error);
      throw error;
    }
  }

  /**
   * Search for words by prefix
   */
  async search(query: string, limit = 10, options: Omit<LookupOptions, 'generateAudio'> = {}): Promise<DictionarySearchResult[]> {
    const {
      phoneticSystem = getUserPhoneticSystem(),
      locale = LOCALE
    } = options;

    try {
      const response = await baseApiClient.get('/dictionary/search', {
        params: {
          q: query,
          limit,
          locale,
          ...(phoneticSystem && { phonetic: phoneticSystem })
        }
      });
      return response.data;
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    }
  }

  /**
   * Check dictionary API health
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await baseApiClient.get('/dictionary/health');
      return response.data;
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  }
}

export const dictionaryService = new DictionaryService();
